import type { Callback, IAbortSignal } from '@/type-utils';

/**
 * A utils class that debounces the execution of a function.
 * It delays the execution of the function until after a specified delay
 * has passed since the last time the function was invoked.
 */
export class Debouncer {
  private timeout?: NodeJS.Timeout;
  private controller?: AbortController;

  /**
   * The signal of the AbortController associated with the debouncer.
   * This signal can be used to abort the execution of the function.
   *
   * @returns The signal of the AbortController.
   */
  public get abortControllerSignal(): IAbortSignal | undefined {
    return this.controller?.signal;
  }

  /**
   * Constructor of the Debouncer class.
   *
   * @param delay - The delay in milliseconds to wait before executing the function.
   */
  private constructor(private delay: number) {}

  /**
   * Creates a new Debouncer instance with the specified delay in milliseconds.
   *
   * @param delay - The delay in milliseconds to wait before executing the function.
   * @returns A new Debouncer instance.
   * @example
   * const debouncer = Debouncer.milliseconds(500);
   */
  public static milliseconds(delay: number): Debouncer {
    return new Debouncer(delay);
  }

  /**
   * Creates a new Debouncer instance with the specified delay in seconds.
   *
   * Useful just to simplify the usage of the class when the delay is higher than 1 second.
   *
   * @param delay - The delay in seconds to wait before executing the function.
   * @returns A new Debouncer instance.
   * @example
   * const debouncer = Debouncer.seconds(2);
   */
  public static seconds(delay: number): Debouncer {
    return new Debouncer(delay * 1000);
  }

  /**
   * Executes the provided callback function after the specified delay.
   * If the function is called again before the delay has passed,
   * the previous timeout is cleared and a new one is set.
   *
   * **IMPORTANT**: If doing debounced API calls, make sure to propagate the
   * `AbortSignal` from the `abortControllerSignal` property to the API call to make sure
   * the request will be properly cancelled and not create memory leaks or race conditions.
   *
   * @param callback - The function to be executed after the delay.
   */
  public execute<TResponse = void>(callback: Callback<[], TResponse>): void {
    this.cancel();
    this.controller = new AbortController();
    this.timeout = setTimeout(() => {
      callback();
    }, this.delay);
  }

  /**
   * Cancels the current timeout and aborts the current request.
   * This method is useful to prevent the execution of the callback.
   */
  public cancel(): void {
    if (this.timeout) clearTimeout(this.timeout);
    if (this.controller) this.controller.abort();
  }
}
